import { PageHeader } from '@/features/_components/ui/page-header'
import type { TExposedApplicationAggregateDTO } from '@doutorsim/shared/applications'
import type { TExposedFacilityDTO } from '@doutorsim/shared/facilities'
import type { TExposedPersonDTO } from '@doutorsim/shared/persons'
import type { TCreateUserOutputDTO } from '@doutorsim/shared/users'
import { useQuery } from '@tanstack/react-query'
import { useLocation } from '@tanstack/react-router'
import { useBreadcrumbs } from '../../../_hooks/use-breadcrumbs'
import { useIsRouteActive } from '../../../_hooks/use-is-route-active'
import { queries } from '../../../_lib/react-query'

interface PersonHeaderProps {
  application: TExposedApplicationAggregateDTO
  person: TExposedPersonDTO
  facility: TExposedFacilityDTO
  userType?: TCreateUserOutputDTO['userType']
}

export const PersonHeader = ({ application, person, facility, userType }: PersonHeaderProps) => {
  const { pathname } = useLocation()
  const isPersonFormActive = useIsRouteActive('applications', 'profile')
  const isApplicationInfoActive = useIsRouteActive('applications', 'application-info')
  const isReceiptsActive = useIsRouteActive('applications', 'receipts')
  const isGuarantorActive = useIsRouteActive('applications', 'guarantors')
  const isDataConfirmationActive = useIsRouteActive('applications', 'data-confirmation')

  const { data: company } = useQuery(queries.companies.detail(application.companyId))

  const { data: declarations } = useQuery({
    ...queries.staffApplicationCards.declarations(application.id),
    enabled: userType === 'staff',
  })
  const hasPendingDeclarations = (declarations ?? []).some((d) => d.status === 'pending')

  const isStaffRoute = pathname.startsWith('/applications/')
  const isFacilityRoute = pathname.includes('/facilities/')
  const searchSuffix = isStaffRoute
    ? `?companyId=${application.companyId}&facilityId=${application.facilityId}`
    : ''

  const companySlug = company?.slug ?? application.companyId

  const base = isStaffRoute
    ? `/applications/${application.id}`
    : isFacilityRoute
      ? `/companies/${companySlug}/facilities/${facility.slug}/applications/${application.id}`
      : `/companies/${companySlug}/applications/${application.id}`

  const facilityPersonsHref = `/companies/${companySlug}/facilities/${facility.slug}/applications`
  const companyPersonsHref = `/companies/${companySlug}/applications`

  const backHref = isStaffRoute
    ? '/applications'
    : isFacilityRoute
      ? facilityPersonsHref
      : companyPersonsHref

  useBreadcrumbs(
    company
      ? isStaffRoute
        ? [{ label: 'Pacientes', href: '/applications' }, { label: person.name }]
        : isFacilityRoute
          ? userType === 'staff'
            ? [
                { label: 'Empresas', href: '/companies' },
                {
                  label: company.name,
                  href: `/companies/${company.slug}/facilities`,
                },
                { label: facility.name, href: facilityPersonsHref },
                { label: person.name },
              ]
            : [
                {
                  label: 'Sua Instituição',
                  href: `/companies/${company.slug}/facilities`,
                },
                { label: facility.name, href: facilityPersonsHref },
                { label: person.name },
              ]
          : userType === 'staff'
            ? [
                { label: 'Empresas', href: '/companies' },
                {
                  label: company.name,
                  href: `/companies/${company.slug}/applications`,
                },
                { label: person.name },
              ]
            : [
                {
                  label: 'Pacientes',
                  href: `/companies/${company.slug}/applications`,
                },
                { label: person.name },
              ]
      : [],
  )

  if (!application || !facility || !person) return null

  return (
    <PageHeader
      title={person.name}
      backHref={backHref}
      tabs={[
        ...(userType === 'staff' && hasPendingDeclarations
          ? [
              {
                label: 'Confirmação de dados',
                href: `${base}/data-confirmation${searchSuffix}`,
                isActive: isDataConfirmationActive,
                badge: (
                  <span
                    aria-label="Confirmação pendente"
                    className="relative inline-flex h-1.5 w-1.5"
                  >
                    <span className="absolute -inset-0.5 animate-pulse rounded-full bg-amber-400/40 [animation-duration:2.4s]" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-amber-500" />
                  </span>
                ),
              },
            ]
          : []),
        {
          label: 'Paciente',
          href: `${base}/profile${searchSuffix}`,
          isActive: isPersonFormActive,
        },
        {
          label: 'Solicitação',
          href: `${base}/application-info${searchSuffix}`,
          isActive: isApplicationInfoActive,
        },
        {
          label: 'Anexos',
          href: `${base}/receipts${searchSuffix}`,
          isActive: isReceiptsActive,
        },
        {
          label: 'Avalistas',
          href: `${base}/guarantors${searchSuffix}`,
          isActive: isGuarantorActive,
        },
      ]}
    />
  )
}
